<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";

import actions from "~/actions";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import FormError from "~/components/dumb/FormError.vue";
import { DotsHorizontalIcon } from "~/icons";
import { Placement } from "~/shared/enums";
import type { Option, PropertyAnySelect } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useTenantStore } from "~/stores";
import { getChipColorsHex } from "~/utils/color";
import { makeUuid } from "~/utils/common";
import { makeValidateOptionTitle } from "~/utils/validation";
import { layoutStable, when } from "~/utils/wait";

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();

const props = defineProps<{
  property: PropertyAnySelect;
  option: Option;
  addingDuids?: string[];
}>();

const emit = defineEmits<{
  add: [title: string, parentDuid: string | null, addingDuid?: string];
  addSubitem: [parentDuid: string];
  createOptionUnderneath: [title: string, parentDuid: string | null, option: Option];
  removeEmptyOption: [duid: string];
}>();

const titleInput = ref<HTMLInputElement | null>(null);

const isTouched = ref(false);
const title = ref(props.option.title);
const id = ref(`option-title-${makeUuid()}`);

const trimmedTitle = computed(() => title.value.trim());

const adding = computed(() => props.addingDuids?.includes(props.option.duid) ?? false);

const options = computed(() => dataStore.getOptionList(props.property));

const sections = actions.context.option(props.option, {
  showTitleEditor: true,
  addSubitem: () => emit("addSubitem", props.option.duid),
});
const onContextMenu = (event: MouseEvent) => {
  if (tenantStore.isDart && !pageStore.adminHidden && event.altKey) {
    return;
  }

  appStore.openContextMenu(event as PointerEvent, sections, { maxHeightPixels: 552 });
};

const colorSet = computed(() => getChipColorsHex(props.option.colorHex, pageStore.theme));

const validate = computed(() =>
  makeValidateOptionTitle((option) =>
    options.value.find(
      (e) =>
        e.title.toLowerCase() === option.trim().toLowerCase() &&
        e.duid !== props.option.duid &&
        e.parentDuid === props.option.parentDuid
    )
  )
);
const validationResult = computed(() => validate.value(trimmedTitle.value));
const isValid = computed(() => (trimmedTitle.value === "" ? false : validationResult.value.isValid));
const errorMsg = computed(() => (validationResult.value.isValid ? "" : validationResult.value.error));

const onBlur = async () => {
  isTouched.value = true;

  if (adding.value) {
    if (trimmedTitle.value === "") {
      emit("removeEmptyOption", props.option.duid);
      return;
    }

    if (isValid.value) {
      emit("add", trimmedTitle.value, props.option.parentDuid, props.option.duid);
    }
    return;
  }

  if (!isValid.value) {
    return;
  }

  if (trimmedTitle.value === props.option.title) {
    return;
  }
  dataStore.updateOption({ duid: props.option.duid, title: trimmedTitle.value });
};

const onEnterPress = () => {
  isTouched.value = true;

  if (adding.value && trimmedTitle.value === "") {
    emit("removeEmptyOption", props.option.duid);
    return;
  }

  if (!isValid.value) {
    titleInput.value?.blur();
    return;
  }

  if (adding.value) {
    if (props.option.parentDuid !== null) {
      emit("add", trimmedTitle.value, props.option.parentDuid);
      emit("addSubitem", props.option.parentDuid);
    } else {
      emit("createOptionUnderneath", trimmedTitle.value, null, props.option);
    }
  } else {
    if (trimmedTitle.value !== props.option.title) {
      dataStore.updateOption({ duid: props.option.duid, title: trimmedTitle.value });
    }
    titleInput.value?.blur();
  }
};

watch(
  () => props.option.title,
  (newValue) => {
    title.value = newValue ?? "";
  }
);

watch(
  () => props.addingDuids,
  async () => {
    if (adding.value) {
      isTouched.value = false;
      await when(titleInput);
      titleInput.value?.focus();
    }
  }
);

onMounted(async () => {
  actions.visualization.openKeyboardIfIos();
  if (adding.value && titleInput.value) {
    isTouched.value = false;
    await layoutStable(titleInput.value);
    titleInput.value?.focus();
  }
});
</script>

<template>
  <div class="relative flex items-center gap-1 select-none">
    <div
      class="flex h-5 max-w-full items-center overflow-hidden rounded-sm border py-1 pr-0.5 pl-1.5 select-none"
      :style="{ color: colorSet.fore, backgroundColor: colorSet.back, borderColor: colorSet.border }"
      @contextmenu="onContextMenu">
      <label :for="id" class="sr-only">Title</label>
      <input
        :id="id"
        ref="titleInput"
        v-model="title"
        v-auto-width="{ minWidthPx: 10 }"
        :title="title"
        type="text"
        class="appearance-none truncate border-none bg-transparent p-0 text-xs font-normal focus-ring-none"
        @blur="onBlur"
        @keydown.enter="onEnterPress" />
    </div>
    <div class="flex flex-1 justify-center">
      <FormError :msg="errorMsg" :show="isTouched && errorMsg.length > 0" />
    </div>
    <DropdownMenu
      v-if="!adding"
      :sections="sections"
      :placement="Placement.BOTTOM_RIGHT"
      :distance="0"
      :max-height-pixels="552">
      <div class="rounded-sm border border-transparent !p-px text-lt hover:bg-lt">
        <span class="sr-only">Options</span>
        <DotsHorizontalIcon class="icon-sm" />
      </div>
    </DropdownMenu>
    <div v-else class="w-4" />
  </div>
</template>
